import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Tree } from 'react-arborist';
import {
  Buildings, Package, UserCircle, UsersThree, Desktop, CaretRight, CaretDown, CaretLeft, MagnifyingGlass,
  Laptop, Monitor, Keyboard, Mouse, HardDrives, Globe, Cloud, Plug, Printer, DeviceMobile,
  Headphones, FolderSimple, X, Info,
} from '@phosphor-icons/react';
import { SkeletonBlock, EmptyState } from '../../../components/module-ui';
import UserAssets from '../../../components/UserAssets';
import {
  listCompanies, listAssetTeams, getCompaniesSummary, listCompanyEmployees, listCompanyAssets,
} from '../api';
import { ROLE_BADGE, STATUS_BADGE, Detail, fmtLabel, fmtDate } from '../ui';

const PAGE_SIZE = 10;

const TONE = {
  employees: { bg: '#EFF6FF', iconBg: '#DBEAFE', icon: '#2563EB', border: '#BFDBFE' },
  products: { bg: '#FFFBEB', iconBg: '#FEF3C7', icon: '#B45309', border: '#FDE68A' },
  assets: { bg: '#ECFDF5', iconBg: '#D1FAE5', icon: '#059669', border: '#A7F3D0' },
};

const CATEGORY_ICON = {
  laptop: Laptop, desktop: Desktop, cpu: Desktop, monitor: Monitor, keyboard: Keyboard, mouse: Mouse,
  server: HardDrives, domain: Globe, hosting: Cloud, cloud: Cloud, cable: Plug, charger: Plug,
  printer: Printer, mobile: DeviceMobile, phone: DeviceMobile, headset: Headphones, headphones: Headphones,
};
const categoryKey = (a) => (a.asset_type || 'uncategorized').toLowerCase();
const categoryLabel = (key) => fmtLabel(key).replace(/\b\w/g, (ch) => ch.toUpperCase());
const categoryIcon = (key) => CATEGORY_ICON[key] || Package;

const TYPE_ICON = {
  company: Buildings, employees: UsersThree, assets: Desktop, employee: UserCircle,
};

/** Group assets by category, largest group first. */
function groupByCategory(assets) {
  const groups = {};
  assets.forEach((a) => { (groups[categoryKey(a)] = groups[categoryKey(a)] || []).push(a); });
  return Object.entries(groups)
    .map(([key, rows]) => ({ key, label: categoryLabel(key), rows }))
    .sort((x, y) => y.rows.length - x.rows.length || x.label.localeCompare(y.label));
}

/**
 * Company Hierarchy, laid out like Explorer. Tree on the left:
 *   Company -> Employees -> each employee
 *           -> Assets -> each category -> each asset
 * Right pane shows the selected node; the Employees / Assets / category views
 * are searchable, paginated tables. Products with no company roll up under
 * "Unassigned".
 */
async function loadCompanyTree() {
  const [companies, products, summary] = await Promise.all([
    listCompanies(), listAssetTeams(), getCompaniesSummary().catch(() => []),
  ]);
  const summaryById = Object.fromEntries(summary.map((r) => [r.company_id, r]));
  const unassignedCount = products.filter((p) => p.product_id && !p.company_id).length;
  const list = [
    ...companies.map((c) => ({
      id: c.company_id,
      name: c.name,
      raw: c,
      productCount: products.filter((p) => p.company_id === c.company_id).length,
    })),
    ...(unassignedCount > 0 ? [{
      id: 'unassigned',
      name: 'Unassigned',
      raw: { description: 'Products with no company assigned yet.' },
      productCount: unassignedCount,
    }] : []),
  ];

  return Promise.all(list.map(async (c) => {
    const [employees, assets] = await Promise.all([
      listCompanyEmployees(c.id).catch(() => []),
      listCompanyAssets(c.id).catch(() => []),
    ]);
    const s = summaryById[c.id] || {};
    const categories = groupByCategory(assets);
    return {
      id: `company:${c.id}`,
      type: 'company',
      name: c.name,
      companyId: c.id,
      raw: c.raw,
      productCount: c.productCount,
      employeeCount: s.employee_count ?? employees.length,
      assetCount: s.asset_count ?? assets.length,
      categories,
      children: [
        {
          id: `employees:${c.id}`,
          type: 'employees',
          name: `Employees (${employees.length})`,
          companyId: c.id,
          companyName: c.name,
          rows: employees,
          children: employees.map((m) => ({
            id: `employee:${c.id}:${m.user_id}`,
            type: 'employee',
            name: m.name,
            userId: m.user_id,
            raw: m,
          })),
        },
        {
          id: `assets:${c.id}`,
          type: 'assets',
          name: `Assets (${assets.length})`,
          companyId: c.id,
          companyName: c.name,
          rows: assets,
          categories,
          children: categories.map((g) => ({
            id: `category:${c.id}:${g.key}`,
            type: 'category',
            name: `${g.label} (${g.rows.length})`,
            categoryKey: g.key,
            categoryLabel: g.label,
            companyId: c.id,
            companyName: c.name,
            rows: g.rows,
            children: g.rows.map((a) => ({
              id: `asset:${c.id}:${a.asset_id}`,
              type: 'asset',
              name: a.asset_tag ? `${a.asset_tag} · ${a.name}` : a.name,
              raw: a,
            })),
          })),
        },
      ],
    };
  }));
}

// ------------------------------------------------------------------ tree --

function Row({ node, style }) {
  const { data } = node;
  let Icon = TYPE_ICON[data.type] || Package;
  if (data.type === 'category') Icon = categoryIcon(data.categoryKey) || FolderSimple;
  if (data.type === 'asset') Icon = categoryIcon(categoryKey(data.raw || {}));
  return (
    <div
      style={{
        ...style,
        display: 'flex', alignItems: 'center', gap: '6px', paddingRight: '8px', cursor: 'pointer',
        background: node.isSelected ? '#EFF6FF' : 'transparent', borderRadius: '6px',
      }}
      onClick={node.handleClick}
    >
      {node.isInternal ? (
        <button type="button"
          onClick={(e) => { e.stopPropagation(); node.toggle(); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#9CA3AF', display: 'flex' }}
          aria-label={node.isOpen ? 'Collapse' : 'Expand'}>
          {node.isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
        </button>
      ) : (
        <span style={{ width: '16px', flexShrink: 0 }} />
      )}
      <Icon size={15} color={node.isSelected ? '#002FA7' : '#6B7280'} style={{ flexShrink: 0 }} />
      <span style={{
        fontSize: '12px', fontWeight: node.isSelected || data.type === 'company' ? '600' : '500',
        color: node.isSelected ? '#002FA7' : '#111827',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {data.name}
      </span>
    </div>
  );
}

function Breadcrumb({ node }) {
  const chain = [];
  let n = node;
  while (n) { chain.unshift(n); n = n.parent && !n.parent.isRoot ? n.parent : null; }
  return (
    <p style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '10px' }}>
      {chain.map((c, i) => (
        <span key={c.id}>
          {i > 0 && ' / '}
          <span style={{ color: i === chain.length - 1 ? '#374151' : '#9CA3AF', fontWeight: i === chain.length - 1 ? '600' : '400' }}>
            {c.data.name}
          </span>
        </span>
      ))}
    </p>
  );
}

// ------------------------------------------------------------ building blocks --

function StatCard({ icon: Icon, label, hint, value, tone, onClick, active }) {
  return (
    <div
      className="hover-card"
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
        background: tone.bg, border: `${active ? 2 : 1}px solid ${active ? tone.icon : tone.border}`, borderRadius: '10px',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{
        width: '32px', height: '32px', borderRadius: '8px', background: tone.iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={17} weight="bold" color={tone.icon} />
      </div>
      <div style={{ minWidth: 0 }}>
        <p style={{ fontSize: '17px', fontWeight: '700', color: '#111827', lineHeight: 1.1 }}>{value}</p>
        <p style={{ fontSize: '11px', color: '#374151', fontWeight: '600' }}>{label}</p>
        {hint && <p style={{ fontSize: '10px', color: '#6B7280' }}>{hint}</p>}
      </div>
    </div>
  );
}

function DetailHeader({ icon: Icon, tone, name, subtitle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
      <div style={{
        width: '32px', height: '32px', borderRadius: '8px', background: tone.iconBg, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={17} weight="bold" color={tone.icon} />
      </div>
      <div style={{ minWidth: 0 }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700' }}>{name}</h3>
        {subtitle && <p style={{ fontSize: '12px', color: '#6B7280' }}>{subtitle}</p>}
      </div>
    </div>
  );
}

function HelpNote({ children }) {
  return (
    <div style={{
      display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '8px 10px', marginBottom: '12px',
      background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '11px', color: '#4B5563',
    }}>
      <Info size={14} color="#6B7280" style={{ flexShrink: 0, marginTop: '1px' }} />
      <span>{children}</span>
    </div>
  );
}

function SearchBar({ placeholder, onSearch, children }) {
  const [text, setText] = useState('');
  // Results update as you type; the button / Enter just re-applies the same text.
  const change = (value) => { setText(value); onSearch(value.trim()); };
  const submit = (e) => { e.preventDefault(); onSearch(text.trim()); };
  const clear = () => change('');
  return (
    <form onSubmit={submit} style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '6px', flex: '1 1 220px',
        border: '1px solid #D1D5DB', borderRadius: '6px', padding: '6px 8px', background: 'white',
      }}>
        <MagnifyingGlass size={14} color="#9CA3AF" />
        <input
          value={text}
          onChange={(e) => change(e.target.value)}
          placeholder={placeholder}
          style={{ border: 'none', outline: 'none', fontSize: '12px', flex: 1, minWidth: 0 }}
        />
        {text && (
          <button type="button" onClick={clear} aria-label="Clear search"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#9CA3AF' }}>
            <X size={13} />
          </button>
        )}
      </div>
      <button type="submit" data-testid="hierarchy-search-button" style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px',
        background: '#002FA7', color: 'white', border: 'none', borderRadius: '6px',
        fontSize: '12px', fontWeight: '600', cursor: 'pointer',
      }}>
        <MagnifyingGlass size={13} weight="bold" /> Search
      </button>
      {children}
    </form>
  );
}

function Pagination({ page, total, onPage }) {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (total === 0) return null;
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);
  const btn = (disabled, active) => ({
    minWidth: '28px', padding: '4px 8px', fontSize: '12px', borderRadius: '6px',
    border: `1px solid ${active ? '#002FA7' : '#D1D5DB'}`,
    background: active ? '#002FA7' : 'white', color: active ? 'white' : disabled ? '#D1D5DB' : '#374151',
    cursor: disabled ? 'default' : 'pointer',
  });
  // Show at most 5 page numbers around the current page.
  const start = Math.max(1, Math.min(page - 2, pages - 4));
  const nums = Array.from({ length: Math.min(5, pages) }, (_, i) => start + i);
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
      <span style={{ fontSize: '12px', color: '#6B7280' }}>Showing {from}–{to} of {total}</span>
      <div style={{ display: 'flex', gap: '4px' }} data-testid="hierarchy-pagination">
        <button type="button" style={btn(page === 1)} disabled={page === 1} onClick={() => onPage(page - 1)}>Prev</button>
        {nums.map((n) => (
          <button key={n} type="button" style={btn(false, n === page)} onClick={() => onPage(n)}>{n}</button>
        ))}
        <button type="button" style={btn(page === pages)} disabled={page === pages} onClick={() => onPage(page + 1)}>Next</button>
      </div>
    </div>
  );
}

const th = {
  textAlign: 'left', padding: '8px 10px', fontSize: '11px', fontWeight: '600', color: '#4B5563',
  background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap',
};
const td = { padding: '8px 10px', fontSize: '12px', borderBottom: '1px solid #F3F4F6', verticalAlign: 'middle' };
const badgeStyle = (bg) => ({
  background: bg || '#F3F4F6', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', textTransform: 'capitalize', whiteSpace: 'nowrap',
});

/** Searchable, paginated table. `columns` = [{ label, render(row) }]. */
function PagedTable({ rows, columns, rowKey, onRowClick, search, emptyText, searchPlaceholder, toolbar, testid }) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return !q ? rows : rows.filter((r) => search(r).some((v) => String(v || '').toLowerCase().includes(q)));
  }, [rows, query, search]);

  useEffect(() => { setPage(1); }, [query, rows]);

  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div data-testid={testid}>
      <SearchBar placeholder={searchPlaceholder} onSearch={setQuery}>{toolbar}</SearchBar>
      {query && (
        <p style={{ fontSize: '11px', color: '#6B7280', marginBottom: '6px' }}>
          {filtered.length} result{filtered.length === 1 ? '' : 's'} for “{query}”
        </p>
      )}
      {filtered.length === 0 ? (
        <EmptyState icon={MagnifyingGlass} title="Nothing to show" description={query ? 'No matches — try a different search.' : emptyText} />
      ) : (
        <>
          <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
              <thead>
                <tr>{columns.map((c) => <th key={c.label} style={th}>{c.label}</th>)}</tr>
              </thead>
              <tbody>
                {pageRows.map((r) => (
                  <tr
                    key={rowKey(r)}
                    className="hover-card"
                    onClick={() => onRowClick(r)}
                    style={{ cursor: 'pointer' }}
                  >
                    {columns.map((c) => <td key={c.label} style={td}>{c.render(r)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} total={filtered.length} onPage={setPage} />
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------- detail views --

const EMPLOYEE_COLUMNS = [
  {
    label: 'Name',
    render: (m) => (
      <div>
        <div style={{ fontWeight: '600', color: '#111827' }}>{m.name}</div>
        {m.email && <div style={{ fontSize: '11px', color: '#6B7280' }}>{m.email}</div>}
      </div>
    ),
  },
  { label: 'Designation', render: (m) => m.designation || '—' },
  { label: 'Role', render: (m) => <span style={badgeStyle(ROLE_BADGE[m.org_role])}>{m.org_role}</span> },
  { label: 'Products', render: (m) => (m.product_names || []).join(', ') || '—' },
  { label: 'Assets held', render: (m) => m.asset_count ?? 0 },
];
const employeeSearch = (m) => [m.name, m.email, m.designation, m.org_role, ...(m.product_names || [])];

const ASSET_COLUMNS = [
  {
    label: 'Asset',
    render: (a) => (
      <div>
        <div style={{ fontWeight: '600', color: '#111827' }}>{a.name}</div>
        <div style={{ fontSize: '11px', color: '#6B7280', fontFamily: 'IBM Plex Mono, monospace' }}>{a.asset_tag || a.asset_id}</div>
      </div>
    ),
  },
  {
    label: 'Category',
    render: (a) => {
      const Icon = categoryIcon(categoryKey(a));
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <Icon size={14} color="#059669" /> {categoryLabel(categoryKey(a))}
        </span>
      );
    },
  },
  { label: 'Serial no.', render: (a) => a.serial_number || '—' },
  { label: 'Assigned to / Client', render: (a) => a.assigned_to_name || a.client_name || 'Not assigned' },
  { label: 'Status', render: (a) => <span style={badgeStyle(STATUS_BADGE[a.status])}>{fmtLabel(a.status)}</span> },
];
const assetSearch = (a) => [a.asset_tag, a.asset_id, a.name, a.asset_type, a.serial_number, a.brand, a.model,
  a.assigned_to_name, a.client_name, a.status];

function CompanyDetail({ node, onOpen }) {
  const { data } = node;
  const [employeesNode, assetsNode] = data.children || [];
  const [view, setView] = useState(null); // 'employees' | 'assets'
  const [assetCategory, setAssetCategory] = useState('all');

  const show = (next, category = 'all') => {
    setAssetCategory(category);
    setView((cur) => (cur === next && category === 'all' ? null : next));
  };

  return (
    <div>
      <DetailHeader icon={Buildings} tone={TONE.products} name={data.name} subtitle={data.raw?.description} />
      <HelpNote>
        <b>Employees</b> are the members of this company's product teams.{' '}
        <b>Assets</b> are devices held by those employees, plus servers, domains and hosting allocated to this
        company's products or clients. Click a card to show the list below.
      </HelpNote>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '10px', marginBottom: '16px' }}>
        <StatCard icon={Package} label="Products" value={data.productCount} tone={TONE.products} />
        <StatCard
          icon={UsersThree} label="Employees" hint={view === 'employees' ? 'Showing list below' : 'Click to view list'}
          value={data.employeeCount} tone={TONE.employees} active={view === 'employees'}
          onClick={() => show('employees')}
        />
        <StatCard
          icon={Desktop} label="Assets" hint={view === 'assets' ? 'Showing list below' : 'Click to view list'}
          value={data.assetCount} tone={TONE.assets} active={view === 'assets'}
          onClick={() => show('assets')}
        />
      </div>

      {view === 'employees' && employeesNode && (
        <Section title={`Employees (${employeesNode.rows.length})`}>
          <EmployeesTable data={employeesNode} onOpen={onOpen} />
        </Section>
      )}
      {view === 'assets' && assetsNode && (
        <Section title={`Assets (${assetsNode.rows.length})`}>
          <AssetsBrowser key={assetCategory} data={assetsNode} initialCategory={assetCategory} onOpen={onOpen} />
        </Section>
      )}
      {!view && data.categories.length > 0 && (
        <Section title="Assets by category">
          <CategoryCards categories={data.categories} onPick={(key) => show('assets', key)} />
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="page-enter" style={{ borderTop: '1px solid #E5E7EB', paddingTop: '12px' }}>
      <p style={{ fontSize: '13px', fontWeight: '700', marginBottom: '8px' }}>{title}</p>
      {children}
    </div>
  );
}

function EmployeesTable({ data, onOpen }) {
  return (
    <PagedTable
      testid="company-employees-table"
      rows={data.rows}
      columns={EMPLOYEE_COLUMNS}
      rowKey={(m) => m.user_id}
      search={employeeSearch}
      searchPlaceholder="Search employees by name, email, role or product…"
      emptyText={`No employees linked to ${data.companyName} yet.`}
      onRowClick={(m) => onOpen({ type: 'employee', userId: m.user_id, raw: m, from: 'Employees' })}
    />
  );
}

function AssetsBrowser({ data, initialCategory = 'all', onOpen }) {
  const [category, setCategory] = useState(initialCategory);
  const rows = category === 'all' ? data.rows : (data.categories.find((g) => g.key === category)?.rows || []);
  return (
    <>
      {data.categories.length > 0 && (
        <CategoryCards
          categories={data.categories}
          active={category}
          onPick={(key) => setCategory((cur) => (cur === key ? 'all' : key))}
        />
      )}
      <PagedTable
        testid="company-assets-table"
        rows={rows}
        columns={ASSET_COLUMNS}
        rowKey={(a) => a.asset_id}
        search={assetSearch}
        searchPlaceholder="Search assets by tag, name, serial no. or assignee…"
        emptyText={`No assets linked to ${data.companyName} yet.`}
        onRowClick={(a) => onOpen({ type: 'asset', raw: a, from: 'Assets' })}
        toolbar={(
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label="Filter by category"
            style={{ padding: '6px 8px', fontSize: '12px', border: '1px solid #D1D5DB', borderRadius: '6px', background: 'white' }}
          >
            <option value="all">All categories ({data.rows.length})</option>
            {data.categories.map((g) => <option key={g.key} value={g.key}>{g.label} ({g.rows.length})</option>)}
          </select>
        )}
      />
    </>
  );
}

function CategoryCards({ categories, active, onPick }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px', marginBottom: '14px' }}>
      {categories.map((g) => (
        <StatCard
          key={g.key}
          icon={categoryIcon(g.key)}
          label={g.label}
          value={g.rows.length}
          tone={TONE.assets}
          active={active === g.key}
          onClick={() => onPick(g.key)}
        />
      ))}
    </div>
  );
}

function EmployeesDetail({ node, onOpen }) {
  const { data } = node;
  return (
    <div>
      <DetailHeader icon={UsersThree} tone={TONE.employees} name={`${data.companyName} — Employees`}
        subtitle={`${data.rows.length} employee${data.rows.length === 1 ? '' : 's'} across this company's product teams`} />
      <HelpNote>Type to search by name, email, designation, role or product. Click a row to see that employee and their assets.</HelpNote>
      <EmployeesTable data={data} onOpen={onOpen} />
    </div>
  );
}

function AssetsDetail({ node, onOpen }) {
  const { data } = node;
  return (
    <div>
      <DetailHeader icon={Desktop} tone={TONE.assets} name={`${data.companyName} — Assets`}
        subtitle={`${data.rows.length} asset${data.rows.length === 1 ? '' : 's'} in ${data.categories.length} categor${data.categories.length === 1 ? 'y' : 'ies'}`} />
      <HelpNote>Pick a category card to narrow the list, or type to search by tag, name, serial number, or who it is assigned to. Click a row for full details.</HelpNote>
      <AssetsBrowser data={data} onOpen={onOpen} />
    </div>
  );
}

function CategoryDetail({ node, onOpen }) {
  const { data } = node;
  return (
    <div>
      <DetailHeader icon={categoryIcon(data.categoryKey)} tone={TONE.assets} name={`${data.companyName} — ${data.categoryLabel}`}
        subtitle={`${data.rows.length} ${data.categoryLabel.toLowerCase()} asset${data.rows.length === 1 ? '' : 's'}`} />
      <PagedTable
        testid="company-category-table"
        rows={data.rows}
        columns={ASSET_COLUMNS.filter((c) => c.label !== 'Category')}
        rowKey={(a) => a.asset_id}
        search={assetSearch}
        searchPlaceholder={`Search ${data.categoryLabel.toLowerCase()}…`}
        emptyText="Nothing in this category."
        onRowClick={(a) => onOpen({ type: 'asset', raw: a, from: data.categoryLabel })}
      />
    </div>
  );
}

function EmployeeDetail({ data }) {
  const m = data.raw || {};
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        {m.picture ? (
          <img src={m.picture} alt="" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          <UserCircle size={36} color="#9CA3AF" />
        )}
        <div>
          <h3 style={{ fontSize: '14px', fontWeight: '700' }}>{m.name}</h3>
          <p style={{ fontSize: '12px', color: '#6B7280' }}>{m.designation || m.email}</p>
        </div>
        <span className="badge" style={{ background: ROLE_BADGE[m.org_role] || '#F3F4F6', fontSize: '11px', textTransform: 'capitalize', marginLeft: 'auto' }}>
          {m.org_role}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '10px' }}>
        <Detail label="Email" value={m.email} />
        <Detail label="Products" value={(m.product_names || []).join(', ')} />
        <Detail label="Client(s)" value={(m.client_names || []).join(', ') || 'None (product-level)'} />
        <Detail label="Assets assigned" value={m.asset_count != null ? String(m.asset_count) : '—'} />
      </div>
      <UserAssets key={data.userId} userId={data.userId} />
    </div>
  );
}

function AssetDetail({ data }) {
  const a = data.raw || {};
  return (
    <div>
      <DetailHeader icon={categoryIcon(categoryKey(a))} tone={TONE.assets} name={a.name} subtitle={a.asset_tag || a.asset_id} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
        <Detail label="Category" value={categoryLabel(categoryKey(a))} />
        <Detail label="Status" value={fmtLabel(a.status)} capitalize />
        <Detail label="Serial number" value={a.serial_number} mono />
        <Detail label="Brand / Model" value={[a.brand, a.model].filter(Boolean).join(' ')} />
        <Detail label="Assigned to" value={a.assigned_to_name} />
        <Detail label="Client" value={a.assigned_client_name || a.client_name} />
        <Detail label="Location" value={a.location} />
        <Detail label="Condition" value={a.condition} capitalize />
        <Detail label="Purchase date" value={a.purchase_date ? fmtDate(a.purchase_date) : null} />
        <Detail label="Warranty expiry" value={a.warranty_expiry ? fmtDate(a.warranty_expiry) : null} />
      </div>
      {a.specs && <div style={{ marginTop: '10px' }}><Detail label="Specs" value={a.specs} block /></div>}
      {a.notes && <Detail label="Notes" value={a.notes} block />}
    </div>
  );
}

function DetailPane({ node }) {
  if (!node) {
    return (
      <EmptyState
        icon={Buildings}
        title="Select a company"
        description="Pick a company from the tree on the left, then open its Employees or Assets (grouped by category)."
      />
    );
  }
  return <NodeDetail key={node.id} node={node} />;
}

/**
 * Right-pane content for one tree node. Clicking a row in any list opens that
 * employee / asset here in the right pane (with a Back button) instead of
 * moving the tree selection; the list stays mounted so its search and page
 * are kept when going back.
 */
function NodeDetail({ node }) {
  const { data } = node;
  const [opened, setOpened] = useState(null); // { type: 'employee' | 'asset', raw, userId?, from }

  return (
    <div className="page-enter">
      <Breadcrumb node={node} />
      {opened && (
        <div className="page-enter">
          <button
            type="button"
            data-testid="hierarchy-back-button"
            onClick={() => setOpened(null)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '12px',
              padding: '5px 10px', fontSize: '12px', fontWeight: '600', color: '#002FA7',
              background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '6px', cursor: 'pointer',
            }}
          >
            <CaretLeft size={12} weight="bold" /> Back to {opened.from || 'list'}
          </button>
          {opened.type === 'employee' && <EmployeeDetail data={opened} />}
          {opened.type === 'asset' && <AssetDetail data={opened} />}
        </div>
      )}
      <div style={{ display: opened ? 'none' : 'block' }}>
        {data.type === 'company' && <CompanyDetail node={node} onOpen={setOpened} />}
        {data.type === 'employees' && <EmployeesDetail node={node} onOpen={setOpened} />}
        {data.type === 'assets' && <AssetsDetail node={node} onOpen={setOpened} />}
        {data.type === 'category' && <CategoryDetail node={node} onOpen={setOpened} />}
        {data.type === 'employee' && <EmployeeDetail data={data} />}
        {data.type === 'asset' && <AssetDetail data={data} />}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------- page --

function CompanyHierarchyPage() {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelected(null);
    try {
      setTree(await loadCompanyTree());
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load company hierarchy');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const searchMatch = useMemo(() => (node, term) => (
    (node.data.name || '').toLowerCase().includes(term.toLowerCase())
  ), []);

  if (loading) return <SkeletonBlock />;
  if (error) return <EmptyState icon={Buildings} title="Could not load" description={error} />;

  return (
    <div data-testid="company-hierarchy-view" style={{ display: 'flex', gap: '14px', height: 'calc(100vh - 220px)', minHeight: '480px' }}>
      <div style={{
        width: '320px', flexShrink: 0, display: 'flex', flexDirection: 'column',
        background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px', overflow: 'hidden',
      }}>
        <p style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
          Companies
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #E5E7EB', borderRadius: '6px', padding: '4px 8px', marginBottom: '8px' }}>
          <MagnifyingGlass size={14} color="#9CA3AF" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter tree…"
            style={{ border: 'none', outline: 'none', fontSize: '12px', flex: 1 }}
          />
          <button
            type="button"
            onClick={load}
            title="Refresh tree"
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#002FA7', padding: '0 4px' }}
          >
            Refresh
          </button>
        </div>
        {tree.length === 0 ? (
          <EmptyState icon={Buildings} title="Nothing yet" description="Add companies and products to see them here." />
        ) : (
          <div style={{ flex: 1 }}>
            <Tree
              data={tree}
              openByDefault={false}
              width="100%"
              height={560}
              rowHeight={28}
              indent={16}
              searchTerm={query}
              searchMatch={searchMatch}
              disableDrag disableDrop disableEdit
              onSelect={(nodes) => setSelected(nodes[0] || null)}
            >
              {Row}
            </Tree>
          </div>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0, background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px', overflowY: 'auto' }}>
        <DetailPane node={selected} />
      </div>
    </div>
  );
}

export default CompanyHierarchyPage;
